import React, { useState, useEffect } from "react"
import {Plus, Bug, Lightbulb, Wrench, Calendar, User, AlertCircle, CheckCircle, Clock} from 'lucide-react'
import { lumi } from "../lib/lumi"

interface Evolution {
  _id: string
  title: string
  description: string
  type: "feature" | "evolution" | "bug"
  status: "pending" | "in_progress" | "completed"
  priority: "low" | "medium" | "high"
  createdBy: string
  createdByName: string
  createdAt: string
  updatedAt: string
}

interface EvolutionManagerProps {
  embedded?: boolean
}

export function EvolutionManager({ embedded = false }: EvolutionManagerProps) {
  const [evolutions, setEvolutions] = useState<Evolution[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [filter, setFilter] = useState<"all" | "feature" | "evolution" | "bug">("all")
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "in_progress" | "completed">("all")
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "feature" as "feature" | "evolution" | "bug",
    priority: "medium" as "low" | "medium" | "high"
  })

  const user = lumi.auth.user

  useEffect(() => {
    fetchEvolutions()
  }, [filter, statusFilter])

  const fetchEvolutions = async () => {
    try {
      let filterQuery: any = {}
      
      if (filter !== "all") {
        filterQuery.type = filter
      }
      
      if (statusFilter !== "all") {
        filterQuery.status = statusFilter
      }

      const response = await lumi.entities.evolutions.list({
        filter: filterQuery,
        sort: { createdAt: -1 },
        limit: 100
      })
      setEvolutions(response.list)
    } catch (error) {
      console.error("Error fetching evolutions:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user || !formData.title.trim()) return

    try {
      await lumi.entities.evolutions.create({
        ...formData,
        status: "pending",
        createdBy: user.userId,
        createdByName: user.userName || user.userEmail || "Utilisateur",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      setFormData({
        title: "",
        description: "",
        type: "feature",
        priority: "medium"
      })
      setShowForm(false)
      fetchEvolutions()
    } catch (error) {
      console.error("Error creating evolution:", error)
    }
  }

  const updateStatus = async (id: string, newStatus: Evolution["status"]) => {
    try {
      await lumi.entities.evolutions.update(id, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      })
      fetchEvolutions()
    } catch (error) {
      console.error("Error updating status:", error)
    }
  }

  const deleteEvolution = async (id: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette évolution ?")) return
    
    try {
      await lumi.entities.evolutions.delete(id)
      fetchEvolutions()
    } catch (error) {
      console.error("Error deleting evolution:", error)
    }
  }

  const getTypeIcon = (type: Evolution["type"]) => {
    switch (type) {
      case "feature": return <Lightbulb className="w-5 h-5" />
      case "evolution": return <Wrench className="w-5 h-5" />
      case "bug": return <Bug className="w-5 h-5" />
    }
  }

  const getTypeColor = (type: Evolution["type"]) => {
    switch (type) {
      case "feature": return "bg-blue-100 text-blue-700 border-blue-200"
      case "evolution": return "bg-purple-100 text-purple-700 border-purple-200"
      case "bug": return "bg-red-100 text-red-700 border-red-200"
    }
  }

  const getStatusIcon = (status: Evolution["status"]) => {
    switch (status) {
      case "pending": return <Clock className="w-4 h-4" />
      case "in_progress": return <AlertCircle className="w-4 h-4" />
      case "completed": return <CheckCircle className="w-4 h-4" />
    }
  }

  const getStatusColor = (status: Evolution["status"]) => {
    switch (status) {
      case "pending": return "bg-gray-100 text-gray-700"
      case "in_progress": return "bg-yellow-100 text-yellow-700"
      case "completed": return "bg-green-100 text-green-700"
    }
  }

  const getPriorityColor = (priority: Evolution["priority"]) => {
    switch (priority) {
      case "low": return "bg-gray-100 text-gray-600"
      case "medium": return "bg-yellow-100 text-yellow-700"
      case "high": return "bg-red-100 text-red-700"
    }
  }

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion des Évolutions</h2>
          <p className="text-gray-600">Nouvelles fonctions, évolutions et bugs</p>
        </div>
        {user?.userRole === "ADMIN" && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all"
          >
            <Plus className="w-5 h-5" />
            Nouvelle évolution
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">Type</label>
          <div className="flex gap-2">
            <button
              onClick={() => setFilter("all")}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === "all" ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Tous
            </button>
            <button
              onClick={() => setFilter("feature")}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === "feature" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Fonctions
            </button>
            <button
              onClick={() => setFilter("evolution")}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === "evolution" ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Évolutions
            </button>
            <button
              onClick={() => setFilter("bug")}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === "bug" ? "bg-red-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Bugs
            </button>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">Statut</label>
          <div className="flex gap-2">
            <button
              onClick={() => setStatusFilter("all")}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === "all" ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Tous
            </button>
            <button
              onClick={() => setStatusFilter("pending")}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === "pending" ? "bg-gray-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              En attente
            </button>
            <button
              onClick={() => setStatusFilter("in_progress")}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === "in_progress" ? "bg-yellow-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              En cours
            </button>
            <button
              onClick={() => setStatusFilter("completed")}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === "completed" ? "bg-green-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Terminé
            </button>
          </div>
        </div>
      </div>

      {/* Add Form */}
      {showForm && (
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-6 rounded-xl border-2 border-purple-200">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Nouvelle évolution</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value as any})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="feature">Nouvelle fonction</option>
                  <option value="evolution">Évolution</option>
                  <option value="bug">Bug</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priorité
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value as any})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="low">Basse</option>
                  <option value="medium">Moyenne</option>
                  <option value="high">Haute</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                Ajouter
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Evolutions List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
            <p className="text-gray-600 mt-4">Chargement...</p>
          </div>
        ) : evolutions.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <Lightbulb className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">Aucune évolution trouvée</p>
            <p className="text-gray-500 text-sm mt-2">Ajoutez une nouvelle évolution pour commencer</p>
          </div>
        ) : (
          evolutions.map((evolution) => (
            <div key={evolution._id} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3 flex-1">
                  <div className={`p-2 rounded-lg border-2 ${getTypeColor(evolution.type)}`}>
                    {getTypeIcon(evolution.type)}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{evolution.title}</h3>
                    {evolution.description && (
                      <p className="text-gray-600 leading-relaxed mb-3">{evolution.description}</p>
                    )}
                    
                    <div className="flex flex-wrap gap-2 items-center">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(evolution.status)}`}>
                        {getStatusIcon(evolution.status)}
                        {evolution.status === "pending" ? "En attente" : evolution.status === "in_progress" ? "En cours" : "Terminé"}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(evolution.priority)}`}>
                        {evolution.priority === "low" ? "Priorité basse" : evolution.priority === "medium" ? "Priorité moyenne" : "Priorité haute"}
                      </span>
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                        <User className="w-3 h-3" />
                        {evolution.createdByName}
                      </span>
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                        <Calendar className="w-3 h-3" />
                        {new Date(evolution.createdAt).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {user?.userRole === "ADMIN" && (
                <div className="flex gap-2 pt-4 border-t border-gray-200">
                  {evolution.status !== "pending" && (
                    <button
                      onClick={() => updateStatus(evolution._id, "pending")}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                    >
                      En attente
                    </button>
                  )}
                  {evolution.status !== "in_progress" && (
                    <button
                      onClick={() => updateStatus(evolution._id, "in_progress")}
                      className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg text-sm font-medium hover:bg-yellow-200 transition-colors"
                    >
                      En cours
                    </button>
                  )}
                  {evolution.status !== "completed" && (
                    <button
                      onClick={() => updateStatus(evolution._id, "completed")}
                      className="px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition-colors"
                    >
                      Terminé
                    </button>
                  )}
                  <button
                    onClick={() => deleteEvolution(evolution._id)}
                    className="ml-auto px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
                  >
                    Supprimer
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}